import React from 'react';
import { Navbar, Button, Container, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useModuleConfig } from '../context/ModuleConfigContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkGlobalUnsavedChanges } from '../pages/UserAccountPage';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { getApiUrl } from '../config/api';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { enabledModules, activeModule, setActiveModule, isLoading } = useModuleConfig();
  const { isAuthenticated, logout, user, updateUserSettings } = useAuth();
  const permissions = useUserPermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: 'nl', name: 'Nederlands', flag: 'NL' },
    { code: 'en', name: 'English', flag: 'EN' },
    { code: 'de', name: 'Deutsch', flag: 'DE' },
    { code: 'fr', name: 'Français', flag: 'FR' },
    { code: 'it', name: 'Italiano', flag: 'IT' },
    { code: 'es', name: 'Español', flag: 'ES' },
    { code: 'pl', name: 'Polski', flag: 'PL' },
    { code: 'da', name: 'Dansk', flag: 'DA' },
    { code: 'sv', name: 'Svenska', flag: 'SV' },
    { code: 'fi', name: 'Suomi', flag: 'FI' },
    { code: 'no', name: 'Norsk', flag: 'NO' }
  ];

  const changeLanguage = async (langCode: string) => {
    i18n.changeLanguage(langCode);
    
    // If authenticated, save to user settings
    if (isAuthenticated && user && user.settings) {
      try {
        const response = await fetch(getApiUrl('/api/web/user/settings'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            language: langCode
          })
        });

        if (response.ok) {
          // Update local user settings
          updateUserSettings({
            ...user.settings,
            language: langCode
          });
        } else {
          console.error('Failed to save language preference:', response.statusText);
        }
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Navbar variant="dark" className="py-3" style={{ backgroundColor: '#101010' }}>
        <Container>
          <div className="text-center">
            <span className="text-light">{t('common.loading')}</span>
          </div>
        </Container>
      </Navbar>
    );
  }


  return (
    <Navbar variant="dark" className="py-3" style={{ backgroundColor: '#101010' }}>
      <Container>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center">
            <button
              type="button"
              onClick={() => {
                if (checkGlobalUnsavedChanges()) {
                  // Always do a full redirect to ensure correct page is shown
                  window.location.href = '/';
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer'
              }}
              aria-label="Home"
            >
              <img src="/img/kovvar-logo.png" alt="Kovvar Logo" style={{height: '40px'}} />
            </button>
            {isAuthenticated && permissions.canAccessProducts && (
              <div className="header-nav ms-4">
                {enabledModules.map((module) => (
                  <button
                    key={module.id}
                    type="button"
                    className={`nav-link me-3 ${activeModule?.id === module.id && location.pathname === '/' ? 'active' : ''}`}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      borderRadius: '0',
                      padding: '0.5rem 0',
                      borderBottom: activeModule?.id === module.id && location.pathname === '/' ? '1px solid #ffffff' : '1px solid transparent'
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      // Check for unsaved changes before navigating
                      if (checkGlobalUnsavedChanges()) {
                        setActiveModule(module.id);
                        // Navigate to home if we're not already there
                        if (location.pathname !== '/') {
                          navigate('/');
                        }
                      }
                    }}
                  >
                    {t(module.name)}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="user-controls">
              {isAuthenticated ? (
                <div className="d-flex align-items-center">
                  <span className="text-light me-3">
                    {user?.email}
                  </span>
                  
                  {/* Language Dropdown */}
                  <Dropdown align="end" className="me-2">
                    <Dropdown.Toggle 
                      variant="outline-light" 
                      size="sm"
                      id="language-dropdown"
                      className="border-0"
                    >
                      {languages.find(lang => lang.code === i18n.language)?.flag || 'EN'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ zIndex: 1030 }}>
                      {languages.map((lang) => (
                        <Dropdown.Item 
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          active={i18n.language === lang.code}
                        >
                          {lang.flag} {lang.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* User Dropdown */}
                  <Dropdown align="end">
                    <Dropdown.Toggle 
                      variant="outline-light" 
                      size="sm"
                      id="user-dropdown"
                      className="d-flex align-items-center"
                    >
                      <i className="fas fa-user me-2"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ zIndex: 1030 }}>
                      <Dropdown.Item key="orders" onClick={() => {
                        if (checkGlobalUnsavedChanges()) {
                          navigate('/orders');
                        }
                      }}>
                        <i className="fas fa-history me-2"></i>
                        {t('nav.orders')}
                      </Dropdown.Item>
                      <Dropdown.Item key="settings" onClick={() => {
                        if (checkGlobalUnsavedChanges()) {
                          navigate('/account');
                        }
                      }}>
                        <i className="fas fa-cog me-2"></i>
                        {t('nav.settings')}
                      </Dropdown.Item>
                      <Dropdown.Divider key="divider" />
                      <Dropdown.Item key="logout" onClick={() => logout()}>
                        <i className="fas fa-sign-out-alt me-2"></i>
                        {t('nav.logout')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              ) : (
                <div className="d-flex align-items-center">
                  {/* Language Dropdown for non-authenticated users */}
                  <Dropdown align="end" className="me-2">
                    <Dropdown.Toggle
                      variant="outline-light"
                      size="sm"
                      id="language-dropdown-guest"
                      className="border-0"
                    >
                      {languages.find(lang => lang.code === i18n.language)?.flag || 'EN'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ zIndex: 1030 }}>
                      {languages.map((lang) => (
                        <Dropdown.Item
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          active={i18n.language === lang.code}
                        >
                          {lang.flag} {lang.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <Button variant="outline-light" size="sm">
                    {t('nav.login')}
                  </Button>
                </div>
              )}
          </div>
        </div>
      </Container>
    </Navbar>
  );
};

export default Header;